import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { logError } from "./errorLogger";

const _rawClient = generateClient();

function _handleGraphqlResponse(response, skipGlobalError) {
  const { data, errors } = response;
  if (errors?.length > 0) {
    if (!skipGlobalError) {
      if (window.location.hostname !== "localhost") {
        const isUnauthorized = errors.some(
          (e) => e.message?.toLowerCase().includes("authorized") || e.errorType === "Unauthorized"
        );
        if (isUnauthorized && !data) {
          window.location.href = "/error-permission-denied";
          return response;
        }
      }
      // Skip pure Unauthorized errors — these are expected for permission-restricted
      // users and are handled by the permission system, not application bugs.
      const allUnauthorized = errors.every(
        (e) => e.message?.toLowerCase().includes("authorized") || e.errorType === "Unauthorized"
      );
      if (allUnauthorized) return response;

      const msg = errors.map((e) => e.message).filter(Boolean).join("; ") || "GraphQL error";
      logError(msg, { stack: JSON.stringify(errors).slice(0, 2000), componentName: "GraphQL" });
    }
  }
  return response;
}

function _withErrorHandling(result, skipGlobalError) {
  if (!result || typeof result.then !== "function") return result;
  return result
    .then((response) => _handleGraphqlResponse(response, skipGlobalError))
    .catch((error) => {
      if (error.errors) return _handleGraphqlResponse(error, skipGlobalError);
      console.error("Global GraphQL Error:", error);
      if (!skipGlobalError) {
        if (error.status === 401 || error.status === 403) {
          window.location.href = "/error-permission-denied";
        }
        logError(error.message ?? String(error), { stack: error.stack, componentName: "GraphQL" });
      }
      throw error;
    });
}

// Wrapped v6 AppSync client with global error interceptor.
// Usage: client.graphql({ query, variables, skipGlobalError? })
// skipGlobalError is app-level only — stripped before forwarding to v6.
//
// Why authMode: "lambda" here (it is NOT a Lambda-authorizer feature):
//   Our AppSync @auth rules authorize on custom Cognito attributes
//   (identityClaim "custom:companyId" / "custom:userRoleId"). Those claims live
//   ONLY in the Cognito ID token — they are absent from the access token.
//   Amplify v6's authMode:"userPool" sends the ACCESS token by default, which
//   would drop those claims and break every @auth rule (app-wide permission
//   denied). v4 sent the ID token explicitly via graphql_headers
//   (session.getIdToken().getJwtToken()); we reproduce that here.
//
//   In Amplify v6 the only way to send a hand-picked token is to set
//   authMode:"lambda" (or "oidc") and pass it as authToken. We use "lambda".
//   Despite the name, NO Lambda authorizer runs: AppSync sees a valid Cognito
//   JWT in the Authorization header and validates it via the Cognito User Pool
//   provider. (Verified: the API's AWS_LAMBDA authorizer is inert for these
//   requests — routing is identical with or without it.) So do not "simplify"
//   this to authMode:"userPool"; that swaps the token and breaks authorization.
//
//   Subscriptions return Observables (not Promises) and are passed through
//   unchanged.
export const client = {
  graphql(options) {
    const { skipGlobalError = false, ...clientOptions } = options;

    // Subscriptions return an Observable — pass through without async token fetch
    const queryStr = typeof clientOptions.query === "string" ? clientOptions.query.trim() : "";
    if (/^subscription\b/i.test(queryStr)) {
      return _rawClient.graphql(clientOptions);
    }

    // Queries/mutations: fetch ID token to match v4 graphql_headers behavior
    return fetchAuthSession()
      .then((session) => session.tokens?.idToken?.toString() ?? null)
      .catch(() => null)
      .then((idToken) => {
        const authOptions = idToken
          ? { ...clientOptions, authMode: "lambda", authToken: idToken }
          : clientOptions;
        return _withErrorHandling(_rawClient.graphql(authOptions), skipGlobalError);
      });
  },
};
